import React from 'react';
import { getHexToRgb } from 'lib/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { PROGRESS_CIRCLE_SIZE } from '../constants';
import {
  CircleWrapper,
  CircleTrack,
  FilledCircle,
  getCircleSize,
  getPercentLabelSize,
} from '../shared-styles/circularProgressElements';

const DeterminateFilledCircle = styled(FilledCircle)`
  transition: stroke-dashoffset 400ms ease-in;
`;

const ProgressPercentageText = styled.div`
  align-items: center;
  color: ${({ theme }) => `rgba(${getHexToRgb(theme.color.additional.dark.value)}, 0.7)`};
  display: flex;
  font-family: ${({ theme }) => theme.font.family.main.value};
  font-size: ${({ $fontSize }) => `${$fontSize}px`};
  font-style: normal;
  font-weight: ${({ theme }) => theme.font.weight.regular.value};
  height: 100%;
  justify-content: center;
  left: 0;
  line-height: 16px;
  position: absolute;
  right: 0;
  text-align: center;
  top: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.zIndex.progressIndicatorText.value};
`;

const DeterminateProgressCircle = ({
  ariaLabel = 'Loading',
  className,
  dataTestId,
  progress = 0,
  size = PROGRESS_CIRCLE_SIZE.LARGE,
  ...other
}) => {
  const limitedProgress = Math.min(Math.max(progress, 0), 100);

  /**
   * These next values are set up against the 96 pixel svg viewBox to mimic design specs.
   * The progress circle sizes to scale according to its width (which is set according to size prop)...
   *
   * @offset is the number that controls the head of the progress circle.
   *
   */
  const strokeWidth = 9.6; // 20% of half the 96px viewBox width (or 10% of the whole width)
  const radius = 43.2; // half of 96 diameter... minus half of 9.6 strokewidth
  const circumference = radius * 2 * Math.PI; // this is just simple geometry
  const offset = circumference - (limitedProgress / 100) * circumference; // see above

  return (
    <CircleWrapper
      className={className || undefined}
      data-testid={dataTestId || undefined}
      size={getCircleSize(size)}
      {...other}
    >
      <svg
        aria-label={ariaLabel}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={progress}
        className={className ? `${className}-circle-wrapper-svg` : undefined}
        data-testid={dataTestId ? `${dataTestId}-circle-wrapper-svg` : undefined}
        height={getCircleSize(size)}
        role="progressbar"
        width={getCircleSize(size)}
        viewBox="0 0 96 96"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <CircleTrack
          className={className ? `${className}-track` : undefined}
          cx="48"
          cy="48"
          data-testid={dataTestId ? `${dataTestId}-track` : undefined}
          r={radius}
          strokeWidth={strokeWidth}
          role="status"
        />
        <DeterminateFilledCircle
          className={className ? `${className}-progress` : undefined}
          cx="48"
          cy="48"
          data-testid={dataTestId ? `${dataTestId}-progress` : undefined}
          r={radius}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          strokeWidth={strokeWidth}
        />
      </svg>
      {size !== PROGRESS_CIRCLE_SIZE.SMALL && (
        <ProgressPercentageText
          $fontSize={getPercentLabelSize(getCircleSize(size))}
          className={className ? `${className}-percent-text` : undefined}
          data-testid={dataTestId ? `${dataTestId}-percent-text` : undefined}
        >
          {limitedProgress}%
        </ProgressPercentageText>
      )}
    </CircleWrapper>
  );
};

DeterminateProgressCircle.propTypes = {
  /** Adds additional class name to progress circle track */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Sets the Aria Label for the progress circle */
  ariaLabel: PropTypes.string,
  /** Sets the size of the loader to s/l/xl/xxl */
  size: PropTypes.oneOf(Object.values(PROGRESS_CIRCLE_SIZE)),
  /** Determines the width of the circular progress track fill based on percent complete */
  progress: PropTypes.number,
};

export { DeterminateProgressCircle };
